// src/pages/base/Base.utils.js

/**
 * Process mission casualties and update squad members
 * @param {Array} currentSquad - Current squad roster
 * @param {Array} casualties - Casualty data from mission
 * @returns {Array} Updated squad with casualty effects applied
 */
export const processMissionCasualties = (currentSquad, casualties) => {
  if (!casualties || casualties.length === 0) return currentSquad;
  
  return currentSquad.map(soldier => {
    const casualty = casualties.find(c => c.id === soldier.id);
    if (!casualty) return soldier; // Soldier wasn't on mission
    
    if (!casualty.survived) {
      // Mark as KIA
      return { ...soldier, status: 'KIA', health: 0 };
    }
    
    // Update health and experience for survivors
    return { 
      ...soldier, 
      health: casualty.finalHp,
      missionsCompleted: (soldier.missionsCompleted || 0) + 1,
      experience: (soldier.experience || 0) + (casualty.experienceGained || 0)
    };
  });
};

/**
 * Check if any facilities have completed construction
 * @param {Array} facilities - Current facilities list
 * @returns {Array} Facilities that completed today
 */
export const getCompletedFacilities = (facilities) => {
  return facilities.filter(f => f.construction_days_remaining === 0 && f.is_active);
};

/**
 * Get injured squad members (not KIA, health below max)
 * @param {Array} squad - Current squad roster
 * @returns {Array} Injured squad members
 */
export const getInjuredSquadMembers = (squad) => {
  return squad.filter(s => s.health < s.max_health && s.status !== 'KIA');
};

// ============================================================================
// EVENT THEMING LAYER
// ============================================================================

/**
 * Searches all event pools in a theme library for a specific event by ID
 * @param {string} eventId - Event type/ID to find
 * @param {Object} eventLibrary - Theme event library
 * @returns {Object|null} Event template or null if not found
 */
const findEventInLibrary = (eventId, eventLibrary) => {
  if (!eventLibrary || !eventId) return null;

  // Define all possible event pool names
  const poolNames = [
    'SCHEDULED_EVENTS',
    'BACKDROP_EVENTS',
    'THREAT_EVENTS',
    'MORALE_EVENTS',
    'FACTION_EVENTS',
    'DISCOVERY_EVENTS',
    'CAMPAIGN_EVENTS'
  ];

  // Search through all pools
  for (const poolName of poolNames) {
    const pool = eventLibrary[poolName];
    if (!pool || !Array.isArray(pool)) continue;

    const found = pool.find(event => event.id === eventId);
    if (found) return found;
  }

  return null;
};

/**
 * Creates fallback event content when theme template is missing
 * @param {Object} rawEvent - Raw event data from game engine
 * @returns {Object} Fallback themed content
 */
const createFallbackContent = (rawEvent) => {
  // Generate human-readable title from event type
  const titleFromType = rawEvent.type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Create generic description based on category and flags
  let description = `Event: ${titleFromType}`;
  
  if (rawEvent.threat) {
    description = `Threat detected: ${titleFromType}`;
  } else if (rawEvent.bonus) {
    description = `Positive development: ${titleFromType}`;
  }

  // Generate rewards/costs summary
  const parts = [];
  
  if (rawEvent.rewards && Object.keys(rawEvent.rewards).length > 0) {
    const rewardText = Object.entries(rawEvent.rewards)
      .map(([k, v]) => `+${v} ${k}`)
      .join(', ');
    parts.push(rewardText);
  }
  
  if (rawEvent.cost && Object.keys(rawEvent.cost).length > 0) {
    const costText = Object.entries(rawEvent.cost)
      .map(([k, v]) => `-${v} ${k}`)
      .join(', ');
    parts.push(costText);
  }

  if (parts.length > 0) {
    description += ` (${parts.join(', ')})`;
  }

  return {
    title: titleFromType,
    description,
    narrative: null // No narrative for fallback
  };
};

/**
 * Applies theme content to a raw event from the game engine
 * Enriches mechanical data with narrative flavor
 * 
 * @param {Object} rawEvent - Raw event from generateDayEvents()
 * @param {Object} eventLibrary - Theme event library (e.g., devastatorsEventsLibrary)
 * @returns {Object} Fully-themed event ready for UI display
 */
export const applyThemeToEvent = (rawEvent, eventLibrary) => {
  if (!rawEvent) return null;

  // Find the matching event template in the theme library
  const themeTemplate = findEventInLibrary(rawEvent.type, eventLibrary);

  // If no theme template found, use fallback content
  const content = themeTemplate 
    ? {
        title: themeTemplate.title,
        description: themeTemplate.description,
        narrative: themeTemplate.narrative || null
      }
    : createFallbackContent(rawEvent);

  // Merge raw mechanical data with themed narrative content
  return {
    // Core identification
    id: rawEvent.type,
    type: rawEvent.type,
    category: rawEvent.category,

    // Theme narrative content
    title: content.title,
    description: content.description,
    narrative: content.narrative,

    // Mechanical data (from game engine)
    rewards: rawEvent.rewards || {},
    cost: rawEvent.cost || {},
    bonus: rawEvent.bonus || false,
    threat: rawEvent.threat || false,

    // Additional metadata
    tags: rawEvent.tags || [],
    storyHooks: rawEvent.storyHooks || [],
    threatIntensity: rawEvent.threatIntensity || null,
    moraleType: rawEvent.moraleType || null,

    // UI helper: formatted rewards text
    rewardsText: rawEvent.rewards && Object.keys(rawEvent.rewards).length > 0
      ? Object.entries(rawEvent.rewards)
          .map(([k, v]) => `+${v} ${k}`)
          .join(', ')
      : null,

    // UI helper: formatted cost text
    costText: rawEvent.cost && Object.keys(rawEvent.cost).length > 0
      ? Object.entries(rawEvent.cost)
          .map(([k, v]) => `-${v} ${k}`)
          .join(', ')
      : null
  };
};

/**
 * Applies theme to multiple events
 * @param {Array} rawEvents - Array of raw events from game engine
 * @param {Object} eventLibrary - Theme event library
 * @returns {Array} Array of themed events
 */
export const applyThemeToEvents = (rawEvents, eventLibrary) => {
  if (!rawEvents || !Array.isArray(rawEvents)) return [];
  
  return rawEvents
    .map(event => applyThemeToEvent(event, eventLibrary))
    .filter(event => event !== null);
};

/**
 * Legacy function - kept for backward compatibility
 * Format day events for display
 * @param {Array} events - Raw event data
 * @returns {Array} Formatted events
 */
export const formatDayEvents = (events) => {
  if (!events || events.length === 0) return [];
  
  return events.map(event => ({
    ...event,
    rewardsText: event.rewards 
      ? Object.entries(event.rewards)
          .map(([k, v]) => `+${v} ${k}`)
          .join(', ')
      : null
  }));
};